import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useMediaQuery,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Badge,
  Divider
} from '@mui/material';
import {
  FormattedMessage
} from 'react-intl';
import {
  Home as HomeIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  HelpOutline as HelpIcon,
  // Only used by the commented-out Documents nav item below.
  // Description as DocumentsIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { colors, routes } from '../constants';
import { performLogout } from '../utils/authUtils';
import useStore from "../store";
import Store from "../store";

const drawerWidth = 240;

export default function NavBar({ isOpen, toggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const isMobile = useMediaQuery('(max-width:900px)');
  const setActiveUser = useStore((state) => state.setActiveUser);
  const setAuthenticated = useStore((state) => state.setAuthenticated);
  const activeUser = useStore((state) => state.activeUser);

  // Function to determine active nav item based on current path
  const getActiveNavItem = () => {
    const pathname = location.pathname;
    if (pathname === routes.home) return 'home';
    if (pathname.startsWith(routes.surveys)) return 'surveys';
    if (pathname.startsWith(routes.insight)) return 'insight';
    if (pathname.startsWith(routes.documents)) return 'documents';
    if (pathname.startsWith(routes.projects)) return 'projects';
    if (pathname.startsWith(routes.help)) return 'help';
    return null;
  };

  const activeNavItem = getActiveNavItem();

  // localOnly: true keeps the Microsoft session alive ("stay signed in to
  // Microsoft"); the default is a full federated logout.
  const logout = async (localOnly = false) => {
    setActiveUser(null);
    setAuthenticated(false);
    const redirectUrl = await performLogout({ localOnly });
    if (redirectUrl) {
      // Entra full logout: hand off to Microsoft to end its session too.
      window.location.assign(redirectUrl);
      return;
    }
    navigate(routes.login);
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : (isOpen ? "permanent" : "temporary")}
      open={isOpen}
      onClose={toggleSidebar}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(135deg,rgb(88, 155, 228) 0%,rgb(97, 157, 173) 100%)',
          backgroundAttachment: 'fixed',
          color: "#FFF",
          padding: "20px 16px",
          borderRight: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: 'blur(50px)',
          transform: !isMobile && !isOpen ? `translateX(-${drawerWidth}px)` : 'translateX(0)',
          transition: 'all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)',
          // position: 'relative',
          // overflow: 'hidden',
          // '&::before': {
          //   content: '""',
          //   position: 'absolute',
          //   top: 0,
          //   left: 0,
          //   right: 0,
          //   bottom: 0,
          //   background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          //   pointerEvents: 'none'
          // },
          // '&::after': {
          //   content: '""',
          //   position: 'absolute',
          //   top: 0,
          //   left: 0,
          //   right: 0,
          //   bottom: 0,
          //   background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 50%, rgba(0,0,0,0.05) 100%)',
          //   pointerEvents: 'none'
          // }
        },
      }}
    >
      {/* Header Section */}
      <Box sx={{
        position: 'relative',
        zIndex: 2,
        mb: 3,
        mt: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>

        <Box sx={{ textAlign: 'center' }}>
          <img
            src="/prompta-cropped.png"
            alt="Prompta AI"
            style={{
              height: '45px',
              width: 'auto',
              maxWidth: '250px',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{
        position: 'relative',
        zIndex: 2,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}>

        <Box sx={{
          position: 'relative',
          zIndex: 2,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '2px',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.5)',
            },
          },
        }}>
          <Box sx={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.15)',
            p: 2,
          }}>

            <List sx={{ py: 0 }}>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => navigate(routes.home)}
                  onMouseEnter={() => setHoveredItem('home')}
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    borderRadius: '12px',
                    mx: 1,
                    mb: 0.5,
                    backgroundColor: activeNavItem === 'home' || hoveredItem === 'home' ? 'rgba(255,255,255,0.15)' : 'transparent',
                    backdropFilter: activeNavItem === 'home' || hoveredItem === 'home' ? 'blur(10px)' : 'none',
                    border: activeNavItem === 'home' || hoveredItem === 'home' ? '1px solid rgba(255,255,255,0.2)' : 'none',
                    transform: activeNavItem === 'home' || hoveredItem === 'home' ? 'translateX(4px)' : 'translateX(0)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transform: 'translateX(4px)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: activeNavItem === 'home' || hoveredItem === 'home' ? '#fff' : 'rgba(255,255,255,0.9)',
                    minWidth: 40,
                    transition: 'all 0.3s ease'
                  }}>
                    <HomeIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<FormattedMessage id="general.home" />}
                    sx={{
                      '& .MuiTypography-root': {
                        color: activeNavItem === 'home' || hoveredItem === 'home' ? '#fff' : 'rgba(255,255,255,0.9)',
                        fontWeight: activeNavItem === 'home' || hoveredItem === 'home' ? 600 : 500,
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {activeUser?.is_staff && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => navigate(routes.surveys)}
                    onMouseEnter={() => setHoveredItem('surveys')}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      borderRadius: '12px',
                      mx: 1,
                      mb: 0.5,
                      backgroundColor: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? 'rgba(255,255,255,0.15)' : 'transparent',
                      backdropFilter: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? 'blur(10px)' : 'none',
                      border: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? '1px solid rgba(255,255,255,0.2)' : 'none',
                      transform: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? 'translateX(4px)' : 'translateX(0)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? '#fff' : 'rgba(255,255,255,0.9)',
                      minWidth: 40,
                      transition: 'all 0.3s ease'
                    }}>
                      <AnalyticsIcon sx={{ fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<FormattedMessage id="general.surveys" />}
                      sx={{
                        '& .MuiTypography-root': {
                          color: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? '#fff' : 'rgba(255,255,255,0.9)',
                          fontWeight: activeNavItem === 'surveys' || hoveredItem === 'surveys' ? 600 : 500,
                          fontSize: '0.9rem',
                          transition: 'all 0.3s ease'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => navigate(routes.insight)}
                  onMouseEnter={() => setHoveredItem('insight')}
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    borderRadius: '12px',
                    mx: 1,
                    mb: 0.5,
                    backgroundColor: activeNavItem === 'insight' || hoveredItem === 'insight' ? 'rgba(255,255,255,0.15)' : 'transparent',
                    backdropFilter: activeNavItem === 'insight' || hoveredItem === 'insight' ? 'blur(10px)' : 'none',
                    border: activeNavItem === 'insight' || hoveredItem === 'insight' ? '1px solid rgba(255,255,255,0.2)' : 'none',
                    transform: activeNavItem === 'insight' || hoveredItem === 'insight' ? 'translateX(4px)' : 'translateX(0)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transform: 'translateX(4px)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: activeNavItem === 'insight' || hoveredItem === 'insight' ? '#fff' : 'rgba(255,255,255,0.9)',
                    minWidth: 40,
                    transition: 'all 0.3s ease'
                  }}>
                    <AnalyticsIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<FormattedMessage id="general.insight" />}
                    sx={{
                      '& .MuiTypography-root': {
                        color: activeNavItem === 'insight' || hoveredItem === 'insight' ? '#fff' : 'rgba(255,255,255,0.9)',
                        fontWeight: activeNavItem === 'insight' || hoveredItem === 'insight' ? 600 : 500,
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Standalone Documents screen — superseded by the project
                  workspace's Documents tab, which is scoped to one project.
                  Hidden from the nav pending removal of the page itself. */}

              {/* Help Section */}
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => navigate(routes.projects)}
                  onMouseEnter={() => setHoveredItem('projects')}
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    borderRadius: '12px',
                    mx: 1,
                    mb: 0.5,
                    backgroundColor: activeNavItem === 'projects' || hoveredItem === 'projects' ? 'rgba(255,255,255,0.15)' : 'transparent',
                    backdropFilter: activeNavItem === 'projects' || hoveredItem === 'projects' ? 'blur(10px)' : 'none',
                    border: activeNavItem === 'projects' || hoveredItem === 'projects' ? '1px solid rgba(255,255,255,0.2)' : 'none',
                    transform: activeNavItem === 'projects' || hoveredItem === 'projects' ? 'translateX(4px)' : 'translateX(0)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transform: 'translateX(4px)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: activeNavItem === 'projects' || hoveredItem === 'projects' ? '#fff' : 'rgba(255,255,255,0.9)',
                    minWidth: 40,
                    transition: 'all 0.3s ease'
                  }}>
                    <FolderIcon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<FormattedMessage id="general.projects" />}
                    sx={{
                      '& .MuiTypography-root': {
                        color: activeNavItem === 'projects' || hoveredItem === 'projects' ? '#fff' : 'rgba(255,255,255,0.9)',
                        fontWeight: activeNavItem === 'projects' || hoveredItem === 'projects' ? 600 : 500,
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Help Section — hidden per request */}
            </List>
          </Box>

          {/* User Profile & Logout Section */}
          <Box sx={{
            position: 'absolute',
            bottom: 20,
            left: 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            {/* User Info */}
            {activeUser && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 25,
                        height: 25,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        border: '2px solid rgba(255,255,255,0.3)',
                        fontSize: '0.9rem',
                        fontWeight: 600
                      }}
                    >
                      {activeUser.first_name?.[0]}{activeUser.last_name?.[0]}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {activeUser.first_name} {activeUser.last_name}
                      </Typography>
                    </Box>

                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.7rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {activeUser.email}
                    </Typography>
                  </Box>
                </Box>

              </Box>
            )}

            {/* Logout Button */}
            <ListItemButton
              onClick={logout}
              onMouseEnter={() => setHoveredItem('logout')}
              onMouseLeave={() => setHoveredItem(null)}
              sx={{
                borderRadius: '12px',
                backgroundColor: hoveredItem === 'logout' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${hoveredItem === 'logout' ? 'rgba(244, 67, 54, 0.4)' : 'rgba(244, 67, 54, 0.2)'}`,
                transform: hoveredItem === 'logout' ? 'translateY(-2px)' : 'translateY(0)',
                transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.25)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(244, 67, 54, 0.4)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(244, 67, 54, 0.2)',
                },
              }}
            >
              <ListItemIcon sx={{
                color: hoveredItem === 'logout' ? '#ff5252' : 'rgba(255, 255, 255, 0.9)',
                minWidth: 40,
                transition: 'all 0.3s ease'
              }}>
                <LogoutIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={<FormattedMessage id="general.logout" />}
                sx={{
                  '& .MuiTypography-root': {
                    color: hoveredItem === 'logout' ? '#ff5252' : 'rgba(255, 255, 255, 0.9)',
                    fontWeight: hoveredItem === 'logout' ? 600 : 500,
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease'
                  }
                }}
              />
            </ListItemButton>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
